import React from "react"
import { useTranslation } from "react-i18next"
import styles from "../EventDashboardV2.module.scss"
import StatCard from "./StatCard"
import TrafficStrip from "./TrafficStrip"
import { IEvent } from "../../../components/Events/types"
import { StatusFilter } from "./GuestList"
import { invitationStatsFromEvent } from "../utils/invitationStatsFromEvent"

interface OverviewProps {
  event: IEvent
  activeFilter: StatusFilter
  onFilterChange: (filter: StatusFilter) => void
}

const Overview: React.FC<OverviewProps> = ({ event, activeFilter, onFilterChange }) => {
  const { t } = useTranslation()
  const isOpenEvent = event.is_open_event === 1
  const {
    delivered,
    inProgress,
    deliveryTotal,
    rsvpTotal,
    going,
    declined,
    awaitingReply,
    failedContact,
    failedSystem,
  } = invitationStatsFromEvent(event)

  const deliveryPercent =
    deliveryTotal > 0 ? Math.round((delivered / deliveryTotal) * 100) : 0

  const select = (filter: StatusFilter) => {
    onFilterChange(activeFilter === filter ? "all" : filter)
  }

  const deliveredLabel = isOpenEvent
    ? t("eventDashboard.overviewQrDelivered")
    : t("eventDashboardV2.stats.invitationsDelivered")
  const goingLabel = isOpenEvent
    ? t("eventDashboard.overviewRegistered")
    : t("eventDashboardV2.filters.confirmed")

  return (
    <div>
      <h3 className={styles.sectionTitle}>{t("eventDashboardV2.labels.overview")}</h3>
      <div className={styles.overviewGrid}>
        {isOpenEvent && (
          <TrafficStrip
            uniqueVisitors={event.open_event_page_unique_visits ?? 0}
            totalVisits={event.open_event_page_total_visits ?? 0}
            registrations={rsvpTotal}
          />
        )}

        <div className={`${styles.overviewRow} ${styles.overviewRowFour}`}>
          <StatCard
            value={delivered}
            total={deliveryTotal}
            label={deliveredLabel}
            valueColor="Green"
            progress={deliveryPercent}
          />
          <StatCard
            value={inProgress}
            total={deliveryTotal}
            label={t("eventDashboardV2.filters.in_progress")}
            valueColor="Orange"
            linkText={t("eventDashboardV2.actions.view")}
            onClick={() => select("in_progress")}
          />
          <StatCard
            value={failedContact}
            total={deliveryTotal}
            label={t("eventDashboardV2.filters.failed_contact")}
            valueColor="Red"
            linkText={t("eventDashboardV2.actions.view")}
            onClick={() => select("failed_contact")}
          />
          <StatCard
            value={failedSystem}
            total={deliveryTotal}
            label={t("eventDashboardV2.filters.failed_system")}
            valueColor="Orange"
            linkText={t("eventDashboardV2.actions.view")}
            onClick={() => select("failed_system")}
          />
        </div>

        <div className={`${styles.overviewRow} ${styles.overviewRowThree}`}>
          <StatCard
            value={going}
            total={rsvpTotal}
            label={goingLabel}
            valueColor="Green"
            linkText={t("eventDashboardV2.actions.view")}
            onClick={() => select("confirmed")}
          />
          <StatCard
            value={awaitingReply}
            total={rsvpTotal}
            label={t("eventDashboardV2.filters.awaiting_reply")}
            valueColor="Orange"
            linkText={t("eventDashboardV2.actions.view")}
            onClick={() => select("awaiting_reply")}
          />
          <StatCard
            value={declined}
            total={rsvpTotal}
            label={t("eventDashboardV2.filters.declined")}
            valueColor="Red"
            linkText={t("eventDashboardV2.actions.view")}
            onClick={() => select("declined")}
          />
        </div>
      </div>
    </div>
  )
}

export default Overview
